'use client';

import { Sparkles, Box } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { SettingsCard, PickerRow } from '@/shared/ui';
import { ProBadge } from '@/features/subscription';
import { useLimitPaywallStore } from '@/shared/stores/limit-paywall-store';
import { usePaywallPayload } from '@/features/subscription/model/subscription.selectors';
import type { RenderModelFamily } from '@/types/render';

export interface ModelContentProps {
  selectedModelFamily: RenderModelFamily;
  onModelFamilyChange: (family: RenderModelFamily) => void;
  // Layout variant — kept for API parity; both variants share the Apple-grouped
  // row idiom now.
  variant?: 'default' | 'compact';
  closeOnSelect?: () => void;
  /** `has_advanced_render_controls` — TRUE for Pro Pass holders and unlimited
   *  buildings. When FALSE, the ATLAS (advanced) model is locked. */
  advancedUsable?: boolean;
}

/**
 * Model-family picker (AURORA / ATLAS). The picker is browsable by EVERYONE — the
 * Model row always opens it. AURORA stays free for all users. ATLAS is an
 * ADVANCED model per the 2026-07 "non-photo styles + Atlas are advanced controls"
 * gating decision: when the building can't use advanced controls
 * (`!advancedUsable`), the ATLAS card shows a PRO badge and a tap opens the Pro
 * Pass paywall instead of selecting it. Mirrors Apple/Android.
 */
export function ModelContent({
  selectedModelFamily,
  onModelFamilyChange,
  closeOnSelect,
  advancedUsable = true,
}: ModelContentProps) {
  const t = useTranslations('render.settings.model');
  const showLimitPaywall = useLimitPaywallStore((s) => s.showPaywall);
  const showPackPaywall = useLimitPaywallStore((s) => s.showPackPaywall);
  // Server-driven "unlock Atlas" payload — same PaywallPayload the 422 uses,
  // cached on the plan so a locked tap doesn't need a network round-trip. Its
  // primary action is the Facade Kit (2026-08: one product, one tap).
  const modelPaywall = usePaywallPayload('PRO_FEATURE_UNAVAILABLE');

  const models: Array<{
    type: RenderModelFamily;
    icon: typeof Sparkles;
    // ATLAS is an advanced model — locked when the building can't use advanced
    // controls (see 2026-07 gating decision above). AURORA is always free.
    advanced: boolean;
  }> = [
    {
      type: 'AURORA',
      icon: Sparkles,
      advanced: false,
    },
    {
      type: 'ATLAS',
      icon: Box,
      advanced: true,
    },
  ];

  const handleSelect = (model: (typeof models)[number]) => {
    // Locked advanced model → the "unlock Atlas" paywall (the Facade Kit
    // unlocks it) instead of selecting. Without the cached payload (rare — the
    // plan is fetched well before the user reaches this picker) open the Kit
    // sheet directly, which loads its own copy from GET /store: parity with
    // Apple/Android, and still never a product the lock isn't about.
    if (model.advanced && !advancedUsable) {
      if (modelPaywall) {
        showLimitPaywall(modelPaywall, 'PRO_FEATURE_UNAVAILABLE');
      } else {
        showPackPaywall();
      }
      return;
    }
    onModelFamilyChange(model.type);
    closeOnSelect?.();
  };

  return (
    <div className="space-y-4">
      {/* Apple ModelPickerView parity: grouped card with leading icon + title +
          description + trailing checkmark. No section footer. */}
      <SettingsCard>
        {models.map((model) => {
          const isLocked = model.advanced && !advancedUsable;
          const isSelected = !isLocked && selectedModelFamily === model.type;
          const Icon = model.icon;

          const leading = (
            <Icon className="h-5 w-5 text-primary" strokeWidth={1.5} />
          );

          return (
            <PickerRow
              key={model.type}
              title={t(`types.${model.type}.name`)}
              description={t(`types.${model.type}.description`)}
              leading={leading}
              trailing={isLocked ? <ProBadge /> : undefined}
              isSelected={isSelected}
              onClick={() => handleSelect(model)}
            />
          );
        })}
      </SettingsCard>
    </div>
  );
}

// Helper to get subtitle for preview
export function getModelSubtitle(
  selectedModelFamily: RenderModelFamily,
  t: (key: string) => string
): string {
  return t(`types.${selectedModelFamily}.name`);
}
